import { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Checkbox,
  Card,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import { Search, Filter, Download } from 'lucide-react';
import { RewardClaim } from '../../types/reward';

interface RewardsTableProps {
  claims: RewardClaim[];
  onApprove: (claimId: string) => void;
  onReject: (claimId: string) => void;
  onViewDetails: (claimId: string) => void;
  onApproveSelected: (claimIds: string[]) => void;
  onExportData: () => void;
  onRedeem?: (claimId: string) => void;
  onPayout?: (claimId: string) => void;
}

const RewardsTable = ({
  claims,
  onApprove,
  onReject,
  onViewDetails,
  onApproveSelected,
  onExportData,
  onRedeem,
  onPayout,
}: RewardsTableProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(filteredClaims.map((claim) => claim.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (claimId: string) => {
    setSelectedIds((prev) =>
      prev.includes(claimId)
        ? prev.filter((id) => id !== claimId)
        : [...prev, claimId]
    );
  };

  const getStatusColor = (status: RewardClaim['status']) => {
    switch (status) {
      case 'paid':
        return { bg: '#E8F5E9', color: '#2E7D32', label: 'Paid' };
      case 'approved':
        return { bg: '#E3F2FD', color: '#1976D2', label: 'Approved' };
      case 'pending':
        return { bg: '#FFF8E1', color: '#F57C00', label: 'Pending' };
      case 'rejected':
        return { bg: '#FFEBEE', color: '#C62828', label: 'Rejected' };
      default:
        return { bg: '#F5F5F5', color: '#757575', label: status };
    }
  };

  const filteredClaims = claims.filter((claim) => {
    const query = searchQuery.toLowerCase();
    return (
      claim.customerName.toLowerCase().includes(query) ||
      claim.customerEmail?.toLowerCase().includes(query) ||
      claim.campaignName?.toLowerCase().includes(query) ||
      claim.rewardType.toLowerCase().includes(query)
    );
  });

  return (
    <Card
      sx={{
        border: '1px solid #EAECEF',
        borderRadius: 3,
        boxShadow: 'none',
      }}
    >
      {/* Table Header Section */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          borderBottom: '1px solid #EAECEF',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Reward Claims
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredClaims.length} claim{filteredClaims.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search claims..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{
              width: { xs: '100%', sm: 256 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#F9FAFB',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#9CA3AF" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<Filter size={18} />}
            sx={{
              minWidth: 'auto',
              px: 2,
              borderColor: '#E5E7EB',
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#D1D5DB',
                bgcolor: '#F9FAFB',
              },
            }}
          >
            Filter
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download size={18} />}
            onClick={onExportData}
            sx={{
              minWidth: 'auto',
              px: 2,
              borderColor: '#E5E7EB',
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#D1D5DB',
                bgcolor: '#F9FAFB',
              },
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: '#F9FAFB' }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedIds.length === filteredClaims.length && filteredClaims.length > 0}
                  indeterminate={
                    selectedIds.length > 0 && selectedIds.length < filteredClaims.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Customer Details
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Submission Date
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Reward Type
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Reward Description
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: '#6B7280' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClaims.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 8 }}>
                  <Typography color="text.secondary">
                    {searchQuery ? 'No claims found matching your search.' : 'No reward claims available yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredClaims.map((claim) => {
                const statusStyle = getStatusColor(claim.status);
                return (
                  <TableRow
                    key={claim.id}
                    hover
                    selected={selectedIds.includes(claim.id)}
                    sx={{
                      borderBottom: '1px solid #EAECEF',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(claim.id)}
                        onChange={() => handleSelectOne(claim.id)}
                      />
                    </TableCell>
                    
                    {/* Customer Details Column */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {claim.campaignName && (
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.25 }}>
                            {claim.campaignName}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.5 }}>
                          {claim.customerName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', display: 'block' }}>
                          {claim.customerEmail || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    {/* Submission Date Column */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {new Date(claim.submissionDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                    </TableCell>
                    
                    {/* Reward Type Column */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.875rem',
                          textTransform: 'capitalize',
                          fontWeight: 500,
                        }}
                      >
                        {claim.rewardType}
                      </Typography>
                    </TableCell>
                    
                    {/* Reward Description Column */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {claim.rewardDescription || 'N/A'}
                      </Typography>
                    </TableCell>
                    
                    {/* Status Column */}
                    <TableCell>
                      <Chip
                        label={statusStyle.label}
                        size="small"
                        sx={{
                          bgcolor: statusStyle.bg,
                          color: statusStyle.color,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 24,
                        }}
                      />
                    </TableCell>
                    
                    {/* Actions Column */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {/* Redeem button for coupon rewards */}
                        {claim.rewardType.toLowerCase() === 'coupon' && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => onRedeem?.(claim.id)}
                            sx={{
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              bgcolor: '#F3E8FF', // Pastel purple
                              color: '#7C3AED',
                              border: '1px solid #E9D5FF',
                              boxShadow: '0px 2px 4px rgba(124, 58, 237, 0.2), inset 0px 1px 0px rgba(255, 255, 255, 0.5)',
                              borderRadius: '6px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: '#E9D5FF',
                                boxShadow: '0px 3px 6px rgba(124, 58, 237, 0.3), inset 0px 1px 0px rgba(255, 255, 255, 0.6)',
                                transform: 'translateY(-1px)',
                              },
                              '&:active': {
                                transform: 'translateY(0px)',
                                boxShadow: '0px 1px 2px rgba(124, 58, 237, 0.2), inset 0px 1px 2px rgba(0, 0, 0, 0.1)',
                              },
                            }}
                          >
                            Redeem
                          </Button>
                        )}
                        
                        {/* Payout button for cash rewards */}
                        {claim.rewardType.toLowerCase() === 'cash' && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => onPayout?.(claim.id)}
                            sx={{
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              bgcolor: '#DCFCE7', // Pastel green
                              color: '#16A34A',
                              border: '1px solid #BBF7D0',
                              boxShadow: '0px 2px 4px rgba(22, 163, 74, 0.2), inset 0px 1px 0px rgba(255, 255, 255, 0.5)',
                              borderRadius: '6px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: '#BBF7D0',
                                boxShadow: '0px 3px 6px rgba(22, 163, 74, 0.3), inset 0px 1px 0px rgba(255, 255, 255, 0.6)',
                                transform: 'translateY(-1px)',
                              },
                              '&:active': {
                                transform: 'translateY(0px)',
                                boxShadow: '0px 1px 2px rgba(22, 163, 74, 0.2), inset 0px 1px 2px rgba(0, 0, 0, 0.1)',
                              },
                            }}
                          >
                            Payout
                          </Button>
                        )}
                        
                        {/* View Details button - always shown */}
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => onViewDetails(claim.id)}
                          sx={{
                            minWidth: 'auto',
                            px: 1.5,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            bgcolor: '#E0F2FE', // Pastel blue
                            color: '#0369A1',
                            border: '1px solid #BAE6FD',
                            boxShadow: '0px 2px 4px rgba(3, 105, 161, 0.2), inset 0px 1px 0px rgba(255, 255, 255, 0.5)',
                            borderRadius: '6px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: '#BAE6FD',
                              boxShadow: '0px 3px 6px rgba(3, 105, 161, 0.3), inset 0px 1px 0px rgba(255, 255, 255, 0.6)',
                              transform: 'translateY(-1px)',
                            },
                            '&:active': {
                              transform: 'translateY(0px)',
                              boxShadow: '0px 1px 2px rgba(3, 105, 161, 0.2), inset 0px 1px 2px rgba(0, 0, 0, 0.1)',
                            },
                          }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default RewardsTable;
